import LeadForm from 'components/LeadForm';
import VehicleViewModel from 'lib/Presenters/VdpPresenter/Vehicle';
import ListingsCarousel from 'components/Shared/ListingsCarousel';
import { ListingViewModel } from 'components/Shared/ListingsCarousel/types';
import { useContext, useState, useEffect } from 'react';
import { ButtonState } from 'components/Shared/Buttons';
import useEvents from 'lib/hooks/useEvents';
import VdpLeadFormModalOpenedEvent from 'lib/Tracking/Vdp/LeadFormModal/OpenedEvent';
import VdpLeadFormModalClosedEvent from 'lib/Tracking/Vdp/LeadFormModal/ClosedEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import css from 'styled-jsx/css';
import { screen, colors } from 'components/styles/variables';
import closeButton from 'public/static/images/close-button-white.svg';
import { LeadFormFields } from 'types/LeadForm';
import classNames from 'classnames';
import { Features, SourcePages } from 'lib/EventSchema';
import vdpViewEvent, { UserExperience } from 'lib/Tracking/Vdp/ListingImpressions/VdpViewEvent';
import useSubmitted, { LeadSubmission } from 'lib/hooks/useSubmitted';
import Criteo from 'lib/Criteo';
import FacebookPixel from 'lib/FacebookPixel';
import useMonthlyPayment from 'lib/hooks/useMonthlyPayment';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { useAutolistUser } from 'lib/hooks/useAutolistUser';
import { AdInfo } from 'lib/CarGurusAds';
import AdhesionAd from 'components/Ad/AdhesionAd';
import { trackVdpView } from 'lib/GoogleAdWords';
import DirectToVdpContext from 'contexts/DirectToVdpContext';
import CheckAvailabilityTapEvent from 'lib/Tracking/Vdp/LeadFormModal/CheckAvailabilityTapEvent';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';
import { useLeadByVin } from 'lib/hooks/useLeadByVin';
import { useLeadFormInitialData } from 'lib/hooks/useLeadFormInitialData';
import VdpModal from './Modal';
import DirectToVdpCarousel from './PhotoCarousel/DirectToVdpCarousel';
import PhotoCarousel from './PhotoCarousel';
import VehicleSnapshot from './VehicleSnapshot';
import PriceAnalysis from './PriceAnalysis';
import PriceHistory from './PriceHistory';
import VehicleInformation from './VehicleInformation';
import KeyFeatures from './KeyFeatures';
import SellerProfile, { SellerProfileProps } from './SellerProfile';
import PhotoLightbox from './PhotoLightbox';
import VdpLayout from './Layout';
import ContactBar from './ContactBar';
import StickyHeader from './StickyHeader';
import ModalLeadForm from '../ModalLeadForm';
import LeadFormTrackingContext from './LeadFormTrackingContext';
import ExperimentalSellerProfile from './ExperimentalSellerProfile';

const modalStyles = css.resolve`
  .lead-form-modal {
    background: ${colors.WHITE};
    max-width: 500px;
    height: 100% !important;
  }

  @media (min-width: ${screen.smMin}) {
    .lead-form-modal {
      height: auto !important;
      border-radius: 16px !important;
      padding: 26px 16px 30px 16px !important;
      max-width: 456px !important;
      position: inherit !important;
    }
  }
`;

const closeButtonCss = css.resolve`
  .close-button {
    display: none;
  }

  @media (min-width: ${screen.smMin}) {
    .close-button {
      display: block;
      position: fixed;
      background-image: url(${closeButton});
      width: 32px;
      height: 32px;
      top: 23px;
      right: 23px;
    }
  }
i`;

const overlayCss = css.resolve`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.9);
  overflow: hidden;
  z-index: 601;
`;

function LeadFormModal({
  isOpen,
  onClose,
  submitLead,
  leadSubmission,
  vehiclePrice,
  listingId,
}: {
  isOpen: boolean;
  onClose: () => void;
  submitLead: (data: LeadFormFields) => Promise<void>;
  leadSubmission: LeadSubmission;
  vehiclePrice?: number;
  listingId: number;
}): JSX.Element {
  return (
    <VdpModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={classNames(modalStyles.className, 'lead-form-modal')}
      closeButtonClassName={closeButtonCss.className}
      overlayClassName={overlayCss.className}
    >
      <LeadFormTrackingContext.Provider value={{ sourcePage: SourcePages.VdpModalLeadForm }}>
        <ModalLeadForm
          onSubmit={submitLead}
          onClose={onClose}
          leadSubmission={leadSubmission}
          vehiclePrice={vehiclePrice}
          listingId={listingId}
        />
        {closeButtonCss.styles}
        {modalStyles.styles}
      </LeadFormTrackingContext.Provider>
      {overlayCss.styles}
    </VdpModal>
  );
}

type SourceFeature = Features.modalLeadForm | Features.leadForm | Features.quickPicks;
interface VdpProps {
  vehicle: VehicleViewModel;
  similarListings: ListingViewModel[];
  isFavorited: boolean;
  submitLead: (data: LeadFormFields, sourceFeature: SourceFeature) => Promise<void>;
  addFavorite?: () => void;
  removeFavorite?: () => void;
  dealerRemoteId?: string;
  adInfo?: AdInfo;
}

function SellerProfileSection({
  useExperiment,
  props,
}: {
  useExperiment: boolean;
  props: SellerProfileProps;
}): JSX.Element {
  return useExperiment ? <ExperimentalSellerProfile {...props} /> : <SellerProfile {...props} />;
}

function Vdp({
  vehicle,
  similarListings,
  addFavorite = (): void => {},
  removeFavorite = (): void => {},
  submitLead,
  isFavorited,
  dealerRemoteId,
  adInfo,
}: VdpProps): JSX.Element {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const {
    images,
    badges,
    make,
    model,
    year,
    basicInfo,
    price,
    trim,
    mileage,
    city,
    state,
    acceptsLeads,
    phoneNumber,
    vin,
    dealerName,
    mapImageUrl,
    zip,
    address,
    mapUrl,
    website,
    description,
    transferAvailable,
    isRegional,
    location,
    experienceBody,
    experience,
    dealerDisclaimer,
    listingId,
  } = vehicle;

  const [leadFormModalIsOpen, setLeadFormModalIsOpen] = useState(false);
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);
  const monthlyPayment = useMonthlyPayment(price);
  const { isMobile } = useDeviceScreen();
  const { isDirectToVdp } = useContext(DirectToVdpContext);

  function openLeadForm(): void {
    setLeadFormModalIsOpen(true);

    if (trackingParams) {
      logger.log(VdpLeadFormModalOpenedEvent({ trackingParams }));
      logger.log(CheckAvailabilityTapEvent({ trackingParams }));
    }
  }

  function closeLeadForm(): void {
    setLeadFormModalIsOpen(false);
    if (trackingParams) {
      logger.log(VdpLeadFormModalClosedEvent({ trackingParams }));
    }
  }

  function onCloseLightbox(): void {
    setLightboxOpen(false);
    setLightboxIndex(slideIndex);
  }

  const { zip: userZip } = useAutolistUser();

  useEffect(() => {
    logger.log(
      vdpViewEvent({ vin, badges, trackingParams, vdpUserExperience: experience as UserExperience })
    );
    FacebookPixel.trackVdpView(vehicle, userZip, adInfo);
    trackVdpView(vehicle.vin);
    Criteo.trackProduct(vin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sellerProfileProps = {
    dealerName,
    mapImageUrl,
    zip,
    address,
    city,
    state,
    mapUrl,
    phoneNumber,
    website,
    description,
    experienceBody,
    openLeadForm,
    transferAvailable,
    isRegional,
    year,
    make,
    model,
    trim,
    vin,
    dealerDisclaimer,
  };

  const { getDecision } = useOptimizelyDecisions();
  const vdpStickyCta4 = getDecision('vdpStickyCta4');

  const existingLead = !!useLeadByVin(vin);
  const leadInitialData = useLeadFormInitialData({ fromAccount: true });
  const submittedLead = useSubmitted(vin);

  // Conditionally override the leadSubmission state
  const leadSubmission = existingLead ? { isSubmitted: true, ...leadInitialData } : submittedLead;

  return (
    <>
      <LeadFormModal
        isOpen={leadFormModalIsOpen}
        onClose={closeLeadForm}
        leadSubmission={leadSubmission}
        submitLead={async (leadFormFields: LeadFormFields): Promise<void> => {
          submitLead(leadFormFields, Features.modalLeadForm);
          setLeadFormModalIsOpen(false);
        }}
        vehiclePrice={price}
        listingId={listingId}
      />
      <VdpLayout
        dealerRemoteId={dealerRemoteId}
        stickyHeader={
          <StickyHeader
            year={year}
            make={make}
            model={model}
            mileage={mileage}
            city={city}
            state={state}
            primaryPhotoUrl={images[0]}
            price={price}
            monthlyPayment={monthlyPayment}
            submitState={leadSubmission.isSubmitted ? ButtonState.disabled : ButtonState.normal}
            onClick={openLeadForm}
          />
        }
        photoCarousel={
          isDirectToVdp ? (
            <DirectToVdpCarousel
              onSlideChange={setSlideIndex}
              images={images}
              onImageClick={(): void => setLightboxOpen(true)}
              isFavorited={isFavorited}
              favorite={addFavorite}
              unfavorite={removeFavorite}
              badges={badges}
              lightboxIndex={lightboxIndex}
              setLightboxIndex={setLightboxIndex}
              slideIndex={slideIndex}
              setSlideIndex={setSlideIndex}
            />
          ) : (
            <PhotoCarousel
              onSlideChange={setSlideIndex}
              images={images}
              onImageClick={(): void => setLightboxOpen(true)}
              isFavorited={isFavorited}
              favorite={addFavorite}
              unfavorite={removeFavorite}
              badges={badges}
            />
          )
        }
        vehicleSnapshot={
          <VehicleSnapshot
            make={make}
            model={model}
            year={year}
            trim={trim}
            price={price}
            monthlyPayment={monthlyPayment}
            mileage={mileage}
            location={location}
            acceptsLeads={acceptsLeads}
            phoneNumber={phoneNumber}
            vin={vin}
            openLeadForm={openLeadForm}
          />
        }
        priceAnalysis={
          <PriceAnalysis
            vin={vin}
            relativePriceDiff={vehicle.relativePriceDiff}
            price={vehicle.priceFormatted}
          />
        }
        priceHistory={
          <PriceHistory
            timeOnMarket={vehicle.buyerIntelligence.timeOnMarket}
            realTimePriceChanges={vehicle.buyerIntelligence.realTimePriceChanges}
            totalPriceChange={vehicle.buyerIntelligence.totalPriceChange}
          />
        }
        vehicleInformation={<VehicleInformation basicInfo={basicInfo} />}
        keyFeatures={
          <KeyFeatures
            more={vehicle.moreFeatures}
            safety={vehicle.safetyFeatures}
            interior={vehicle.interiorFeatures}
            other={vehicle.otherFeatures}
            exterior={vehicle.exteriorFeatures}
          />
        }
        sellerProfile={
          <SellerProfileSection useExperiment={!!isDirectToVdp} props={{ ...sellerProfileProps }} />
        }
        leadForm={
          <LeadForm
            leadSubmission={leadSubmission}
            onSubmit={(data: LeadFormFields): Promise<void> => submitLead(data, Features.leadForm)}
            dealerPhone={phoneNumber}
            vehiclePrice={price}
            listingId={listingId}
          />
        }
        listingsCarousel={
          similarListings.length !== 0 && <ListingsCarousel listings={similarListings} />
        }
        photoLightBox={
          <PhotoLightbox
            imageUrls={images}
            initialSlide={slideIndex}
            onClose={onCloseLightbox}
            isOpen={lightboxOpen}
            dealerRemoteId={dealerRemoteId}
            onSlideChange={setSlideIndex}
          />
        }
        contactBar={({
          className,
          styles,
        }: {
          className?: string;
          styles?: React.CSSProperties;
        }): JSX.Element => {
          return (
            <ContactBar
              openLeadForm={openLeadForm}
              className={className}
              phoneNumber={vehicle.phoneNumber}
              isSubmitted={leadSubmission.isSubmitted}
              acceptsLeads={acceptsLeads}
              styles={styles}
            />
          );
        }}
      />
      {isMobile ? <AdhesionAd adhereToBottom={!vdpStickyCta4} targeting={7} /> : null}
    </>
  );
}

export default Vdp;
